import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import Header from '../../Header'
import Footer from '../../Footer'
import { FormField } from '../../FormField'
import { documents } from '../../../constants/documents'
import SelectorList from '../../SelectorList'
import { getMobileFormattedDate, validateExpirationDate, validateLength } from '../../DateInput'
import Keyboard from '../../Keyboard'
import { AppState } from '../../../reducers'
import {
  setUIForwardAction,
  setUIBackwardsAction,
  setDriverDocumentTypeAction,
  setDriverExpirationDateAction,
  setDriverDocumentIssuedCountryAction,
  setDriverDocumentNumberAction,
  setDriverIndexAction,
} from '../../../actions'
import { UI } from '../../../constants/ui'
import { CustomCaretProps } from '../../CustomCaret'
import './driversDocument.less'
import { getMode } from '../../../hooks/mode'
import { MODE } from '../../../constants/mode'
import { FormFieldInput } from '../../FormFieldInput'
import { INPUT_TYPE } from '../../../constants/inputTypes'
import { FormFieldCountryInput } from '../../FormFieldCountryInput'

const mapDispatchToProps = {
  setUIForward: setUIForwardAction,
  setUIBackwards: setUIBackwardsAction,
  setDriverDocumentType: setDriverDocumentTypeAction,
  setDriverExpirationDate: setDriverExpirationDateAction,
  setDriverDocumentIssuingCountry: setDriverDocumentIssuedCountryAction,
  setDriverDocumentNumber: setDriverDocumentNumberAction,
  setDriverIndex: setDriverIndexAction,
}
type DispatchProps = typeof mapDispatchToProps

const mapStateToProps = (state: AppState) => ({
  driverDocumentType: state.reservationChange.drivers[0].docType,
  driverExpirationDate: state.reservationChange.drivers[0].docExpirationDate,
  driverDocumentIssuingCountryCode: state.reservationChange.drivers[0].docIssuedCountry,
  driverDocumentNumber: state.reservationChange.drivers[0].docNumber,
  reservation: state.reservation.reservation,
  driverIndex: state.reservationChange.driverIndex,
})

type StateProps = ReturnType<typeof mapStateToProps>
type Props = DispatchProps & StateProps

let driverDocumentIssuingCountryInput: HTMLInputElement | null = null

const Component: React.FC<Props> = ({
  setUIForward,
  setUIBackwards,
  setDriverDocumentType,
  setDriverExpirationDate,
  setDriverDocumentIssuingCountry,
  setDriverDocumentNumber,
  driverDocumentType,
  driverExpirationDate,
  driverDocumentIssuingCountryCode,
  driverDocumentNumber,
  reservation,
  setDriverIndex,
}: Props) => {
  const { t } = useTranslation()
  const [activeInput, setActiveInput] = useState<any>()
  const [isKeyboardShown, setShowKeyboard] = useState(true)
  const [driverDocumentIssuingCountryName, setDriverDocumentIssuingCountryName] = useState('')
  const [expirationDate, setExpirationDate] = useState<{ date: string; dateValidation: boolean; cursorPosition?: number }>({
    date: driverExpirationDate,
    dateValidation: validateExpirationDate(driverExpirationDate),
  })
  const [formatInput, setFormatInput] = useState('')
  const [isCountrySelect, setIsCountrySelect] = useState(false)
  const [isNoCountry, setIsNoCountry] = useState(false)

  const driverDocumentTypeInput = useRef(null)
  const driverExpirationDateInput = useRef<HTMLInputElement>(null)
  const driverDocumentNumberInput = useRef(null)

  const refExpirationDateCaret = useRef<CustomCaretProps>(null)
  const refCountryCaret = useRef<CustomCaretProps>(null)
  const refDocumentNumberCaret = useRef<CustomCaretProps>(null)

  const [, updateState] = React.useState<any>()
  const forceUpdate = useCallback(() => updateState({}), [])

  const mode = getMode()
  const isKioskMode = mode === MODE.KIOSK

  const updateCaretPosition = () => {
    switch (activeInput) {
      case driverExpirationDateInput.current:
        refExpirationDateCaret.current && refExpirationDateCaret.current.updateCaretPosition()
        break
      case driverDocumentIssuingCountryInput:
        refCountryCaret.current && refCountryCaret.current.updateCaretPosition()
        break
      case driverDocumentNumberInput.current:
        refDocumentNumberCaret.current && refDocumentNumberCaret.current.updateCaretPosition()
    }
  }

  const onChangeDriverExpirationDateKiosk = (value: string) => {
    if (value.length <= 10) {
      setExpirationDate({ date: value, dateValidation: validateExpirationDate(value) })
      validateExpirationDate(value) && setDriverExpirationDate(value)
    } else {
      forceUpdate()
    }
  }

  const onChangeDriverExpirationDateDesktop = (value: string) => {
    const dateMaxLength = 10
    if (value.length <= (dateMaxLength + 1) && driverExpirationDateInput && driverExpirationDateInput.current) {
      const data = getMobileFormattedDate(driverExpirationDateInput.current, value, dateMaxLength)
      if (!!data) {
        setExpirationDate({ date: data.value, dateValidation: validateExpirationDate(data.value), cursorPosition: data.cursorPosition })
        validateExpirationDate(data.value) && setDriverExpirationDate(data.value)
      } else {
        forceUpdate()
      }
    } else {
      forceUpdate()
    }
  }

  const onChange = (value: string) => {
    switch (activeInput) {
      case driverExpirationDateInput.current:
        if (isKioskMode) {
          onChangeDriverExpirationDateKiosk(value)
        } else {
          onChangeDriverExpirationDateDesktop(value)
        }
        break
      case driverDocumentIssuingCountryInput:
        setDriverDocumentIssuingCountryName(value)
        setIsCountrySelect(false)
        break

      case driverDocumentNumberInput.current:
        setDriverDocumentNumber(value)
        break
    }
    updateCaretPosition()
  }
  const isInputError =
    !expirationDate.dateValidation ||
    !driverDocumentType ||
    !isCountrySelect ||
    !driverDocumentNumber ||
    !driverDocumentNumber.trim()

  const onClick = () => {
    if (reservation && reservation.drivers.length > 1) {
      setDriverIndex(1)
      setUIForward(UI.DRIVER2_INFORMATION)
    } else {
      setUIForward(UI.SUMMARY)
    }
  }

  const isDateFieldError = !validateExpirationDate(expirationDate.date) && validateLength(expirationDate.date, 10)

  const onSelect = (option: string) => {
    setDriverDocumentIssuingCountry(option)
    setIsCountrySelect(true)
  }

  const clearInputField = (currentInput: any) => {
    setActiveInput(currentInput)
    setFormatInput('')
    switch (currentInput) {
      case driverExpirationDateInput.current:
        setExpirationDate({ date: '', dateValidation: false })
        setFormatInput('date')
        break
      case driverDocumentNumberInput.current:
        setDriverDocumentNumber('')
        break
      case driverDocumentIssuingCountryInput:
        setDriverDocumentIssuingCountryName('')
        setIsCountrySelect(false)
        break
    }
  }

  useEffect(() => {
    setActiveInput(driverDocumentTypeInput.current)
  }, [])


  useEffect(() => {
    if (driverExpirationDateInput && driverExpirationDateInput.current && expirationDate.cursorPosition !== undefined) {
      driverExpirationDateInput.current.selectionStart = expirationDate.cursorPosition
      driverExpirationDateInput.current.selectionEnd = expirationDate.cursorPosition
    }
  }, [expirationDate])

  return (
    <React.Fragment>
      <Header showPageCount back={() => setUIBackwards(UI.DRIVERS_INFORMATION)} />
      <div className="drivers-document">
        <div className="drivers-document-title" dangerouslySetInnerHTML={{ __html: t('drivesDocumentPassenger1Document') }}></div>
        <div className="drivers-document-content">
          <div className="drivers-document-row">
            <FormField
              id="drivers-document-type"
              classFormField="form-field drivers-form-field"
              label={t('documentType')}
            >
              <SelectorList options={documents} value={driverDocumentType} onChange={setDriverDocumentType} />
            </FormField>
            <FormFieldInput
              id="drivers-document-expiration-date"
              type={INPUT_TYPE.DATE}
              classFormField={`drivers-form-field`}
              classInput={'expiration-date'}
              label={t('expirationDate')}
              isClearNeed={!!expirationDate.date}
              clearInputField={clearInputField}
              errors={{ inputError: isDateFieldError }}
              inputRef={driverExpirationDateInput}
              value={expirationDate.date}
              onChange={onChange}
              onFocus={() => {
                setActiveInput(driverExpirationDateInput.current)
                setFormatInput('date')
              }}
              onClick={() => {
                setShowKeyboard(true)
                updateCaretPosition()
              }}
              caretRef={refExpirationDateCaret}
              isActive={activeInput === driverExpirationDateInput.current}
            />
            <FormFieldCountryInput
            id="drivers-document-issuing-country"
            classFormField="country"
            label={t('drivesDocumentIssuingCountry')}
            sublabel={isNoCountry ? t('checkTheSpelling') : ''}
            onChange={onChange}
            onSelect={onSelect}
            setCountry={setDriverDocumentIssuingCountryName}
            countryCode={driverDocumentIssuingCountryCode}
            setIsCountrySelect={setIsCountrySelect}
            isCountrySelect={isCountrySelect}
            value={driverDocumentIssuingCountryName}
            inputRef={driverDocumentIssuingCountryInput}
            httpRef={(el: HTMLInputElement) => driverDocumentIssuingCountryInput = el}
            onFocus={() => {
              setActiveInput(driverDocumentIssuingCountryInput)
              setFormatInput('')
            }}
            onClick={() => {
              setShowKeyboard(true)
              updateCaretPosition()
            }}
            isActive={activeInput === driverDocumentIssuingCountryInput}
            autoFocus={false}
            setIsError={setIsNoCountry}
            clearInputField={clearInputField}
            errors={{inputError: isNoCountry}}
            caretRef={refCountryCaret}
          />
          </div>

          <div className="drivers-document-row">
            <FormFieldInput
              id="drivers-document-number"
              type={INPUT_TYPE.TEXT}
              classFormField={`drivers-form-field`}
              label={t('drivesDocumentNumber')}
              isClearNeed={!!driverDocumentNumber}
              clearInputField={clearInputField}
              errors={{ formFieldError: true }}
              inputRef={driverDocumentNumberInput}
              value={driverDocumentNumber}
              onChange={onChange}
              onFocus={() => {
                setActiveInput(driverDocumentNumberInput.current)
                setFormatInput('')
              }}
              onClick={() => {
                setShowKeyboard(true)
                updateCaretPosition()
              }}
              caretRef={refDocumentNumberCaret}
              isActive={activeInput === driverDocumentNumberInput.current}
            />
            <div className="next-button">
              <button className="main-button" onClick={onClick} disabled={isInputError ? true : false}>
                {t('next')}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isKioskMode && <div className="keyboard-cont">
        {isKeyboardShown && (
          <Keyboard
            input={activeInput}
            onChange={onChange}
            format={formatInput}
            onClose={() => setShowKeyboard(false)}
          />
        )}
      </div>}
      <div className="footer-cont">{(!isKeyboardShown || !isKioskMode) && <Footer />}</div>
    </React.Fragment>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(Component)
